import React, { useEffect, useState } from 'react';
import { Button, FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Modal, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { fetchCampaigns, fetchCaps, fetchContractCallFieldRevenueRules, fetchContractLeadFieldRevenueRules, fetchContracts, fetchLeadRules, fetchSchedule, fetchVerticals } from '../../../utils/leadspedia/api';
import { Contract, Rule } from '../../dashboard/dashboard.component';
import { activeVerticals, verticalIDToCallOffers, verticalIDToLeadOffers, VerticalOffer } from '../../../data/lpdata';
import { LpCampaign } from '../../../components/campaign-list/campaign-list.component';
import { fetchUsers, LPUser } from '../../../utils/firebase/firebase.utils';

const defaultReport1FormFields = {
    report1AdvertiserId: '',
    report1VerticalId: '',
    report1OfferId: '',
}

const Report1: React.FC = () => {
    const [users, setUsers] = useState<LPUser[]>([]);
    const [lpVerticals, setLpVerticals] = useState<any[] | null>([]);
    const [lpCampaigns, setLpCampaigns] = useState<LpCampaign[] | null>(null);
    const [lpType, setLpType] = useState<string>('');
    const [verticalOffers, setVerticalOffers] = useState<VerticalOffer[]>([]);
    const [rules, setRules] = useState<Rule[]>([]);
    const [report1FormFields, setReport1FormFields] = useState(defaultReport1FormFields)
    const { report1AdvertiserId, report1VerticalId, report1OfferId } = report1FormFields

    function getUsers() {
        const userList = fetchUsers();
        userList.then(users => {
            const sortedUsers = users.sort((a: LPUser, b: LPUser) =>
                a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
            );
            setUsers(sortedUsers);
        }).catch(error => {
            console.error('Error fetching users:', error);
        });
    }

    const fetchAndSetVerticals = async () => {
        await fetchVerticals()
            .then((verticals: any) => {
                setLpVerticals(verticals);
            })
            .catch((error: any) => {
                console.error('Error fetching verticals:', error);
            });
    };

    const fetchAndSetRules = async (advertiserId: string) => {
        await fetchLeadRules(advertiserId)
            .then((rules: Rule[]) => {
                console.log("RULES: ", rules);
                setRules(rules);
            })
            .catch((error: any) => {
                console.error('Error fetching rules:', error);
            });
    }

    const getAndSetVerticalOffers = (verticalID: string) => {
        const verticalOffers: VerticalOffer[] = [
            ...(verticalIDToLeadOffers.find(item => verticalID in item)?.[verticalID] || []),
            ...(verticalIDToCallOffers.find(item => verticalID in item)?.[verticalID] || [])
        ];

        setVerticalOffers(verticalOffers);
    };

    const fetchAndSetCampaigns = async (verticalID: string, offerID: string, contractId: number) => {
        try {
            const campaigns: LpCampaign[] = await fetchCampaigns(verticalID, offerID);

            // Create a Rule Set for quick lookups
            const ruleSet = new Set(rules.map(rule => rule.rule === 'Block' ? `${rule.campaignID}-${rule.affiliateID}` : null));
            console.log(ruleSet);

            const caps = await fetchCaps(contractId);
            console.log(caps);

            const contractFieldRevenueRules = lpType === 'leads' ?
                await fetchContractLeadFieldRevenueRules(contractId) :
                await fetchContractCallFieldRevenueRules(contractId);
            console.log("Contract Field revenue Rules:", contractFieldRevenueRules);

            let scheduledPrice = 0;
            const schedule = await fetchSchedule(contractId);
            if (schedule && schedule.length > 0) {
                scheduledPrice = schedule[0].price;
            }

            let bid = 0;
            const filteredCampaigns = campaigns.map((campaign) => {
                const contractFieldRevenueRule = contractFieldRevenueRules.find((r: any) =>
                    r.fieldValue === campaign.campaignID.toString()
                );
                if (contractFieldRevenueRule) {
                    bid = lpType === 'leads' ? contractFieldRevenueRule.price : contractFieldRevenueRule.revenue;
                } else { 
                    bid = scheduledPrice;
                }
                return {
                    ...campaign,
                    isBlocked: ruleSet.has(`${campaign.campaignID}-${campaign.affiliateID}`),
                    cap: caps.find((cap: any) => parseInt(cap.campaignID) === campaign.campaignID)?.cap || null,
                    bid: bid,
                }
            })

            setLpCampaigns(filteredCampaigns);
            console.log('Campaigns: ', campaigns);
        }
        catch (error) {
            console.error('Error fetching campaigns:', error);
        }
    }

    const handleReport1Change = (event: any) => {
        setLpCampaigns(null);
        const { name, value } = event.target
        if (name === 'report1AdvertiserId') {
            fetchAndSetRules(value);
        }
        if (name === 'report1VerticalId') {
            report1FormFields.report1OfferId = ''
            getAndSetVerticalOffers(value);
        }
        if (name === 'report1OfferId') {
            const selectedOffer = verticalOffers.find(offer => offer.id === value);
            if (selectedOffer?.name.includes('Leads')) {
                setLpType('leads');
            } else {
                setLpType('calls');
            }
        }

        setReport1FormFields({ ...report1FormFields, [name]: value })
    }

    const generateReport1 = () => {
        console.log('Generating report 1...');
        console.log(report1FormFields);
        setLpCampaigns(null);
        if (!report1AdvertiserId || !report1VerticalId || !report1OfferId) {
            console.error('Missing required fields to generate report 1');
            alert('Missing required fields to generate report 1');
            return;
        }
        fetchContracts(lpType, report1VerticalId, report1AdvertiserId)
            .then((contracts: Contract[]) => {
                if (contracts && contracts.length > 0 && verticalOffers.length > 0) {
                    console.log('Contracts:', contracts);
                    const selectedOffer = verticalOffers.find(offer => offer.id === report1OfferId);
                    const contractId = getContractID(contracts, selectedOffer?.name || '');
                    if (contractId) {
                        fetchAndSetCampaigns(report1VerticalId, report1OfferId, contractId);
                    }
                }
            })
    }

    const getContractID = (contracts: Contract[], contractName: string): number | null => {
        const normalize = (str: string) => str.replace(/\s+/g, ' ').trim(); // Replace multiple spaces and trim
        const foundContract = contracts.find(contract => normalize(contract.contractName) === normalize(contractName));
        return foundContract ? foundContract.contractID : null;
    }

    useEffect(() => {
        getUsers();
        fetchAndSetVerticals();
    }, []);

    return (
        <React.Fragment>
            <h1>Report 1 - Active Campaigns</h1>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2, md: 4 }}
                sx={{ textAlign: 'left', p: 2, backgroundColor: 'white' }}
            >
                <FormControl size='small' fullWidth>
                    <InputLabel id="report1-advertiser">Advertiser</InputLabel>
                    <Select
                        labelId="report1-advertiser"
                        id="report1-advertiser-select"
                        name="report1AdvertiserId"
                        value={report1AdvertiserId}
                        label="Advertiser"
                        onChange={handleReport1Change}
                    >
                        {users.map(user => (
                            <MenuItem key={user.id} value={user.advertiserId}>{user.displayName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size='small' fullWidth>
                    <InputLabel id="report1-vertical">Vertical</InputLabel>
                    <Select
                        labelId="report1-vertical"
                        id="report1-vertical-select"
                        name="report1VerticalId"
                        value={report1VerticalId}
                        label="Vertical"
                        onChange={handleReport1Change}
                    >
                        {
                            lpVerticals && lpVerticals.length > 0 &&
                            lpVerticals
                                .filter(ver => activeVerticals.includes(ver.verticalID))
                                .map(ver => (
                                    <MenuItem key={ver.verticalID} value={ver.verticalID}>{ver.verticalName}</MenuItem>
                                ))
                        }
                    </Select>
                </FormControl>
                <FormControl size='small' fullWidth>
                    <InputLabel id="report1-offer">Offer</InputLabel>
                    <Select
                        labelId="report1-offer"
                        id="report1-offer-select"
                        name="report1OfferId"
                        value={report1OfferId}
                        label="Vertical"
                        onChange={handleReport1Change}
                    >
                        {verticalOffers.map(offer => (
                            <MenuItem key={offer.id} value={offer.id}>{offer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <div>
                    <Button
                        variant="contained"
                        onClick={generateReport1}
                        size="medium"
                        disabled={!report1AdvertiserId || !report1VerticalId || !report1OfferId}
                    >
                        Generate
                    </Button>
                </div>

            </Stack>
            <Stack>
                {report1AdvertiserId && report1VerticalId && report1OfferId && lpCampaigns && lpCampaigns.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#e5e5e5' }}>
                                    {/* <TableCell>Advertiser</TableCell> */}
                                    <TableCell sx={{fontWeight: 600}}>Vertical</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Offer</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Campaign Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Affiliate</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Cap</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Bid</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lpCampaigns.map((campaign) => (
                                    <TableRow
                                        key={campaign.campaignID}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>{campaign.verticalName}</TableCell>
                                        <TableCell>{campaign.offerName}</TableCell>
                                        <TableCell>{campaign.campaignName}</TableCell>
                                        <TableCell>{campaign.affiliateName}</TableCell>
                                        <TableCell>{campaign.isBlocked ?
                                            (<span style={{color: 'red'}}>Paused</span>) :
                                            (<span style={{color: 'green' }}>Active</span>) }
                                        </TableCell>
                                        <TableCell>{campaign.cap}</TableCell>
                                        <TableCell>{campaign.bid}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography sx={{ backgroundColor: 'white', p: 2 }}>No campaigns found for this report.</Typography>
                )}

            </Stack>
        </React.Fragment>
    );
};

export default Report1;